"use client";

import { cn } from "@/utils/cn";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";

interface CopyButtonProps {
	text: string;
	className?: string;
	iconSize?: "sm" | "md" | "lg";
	variant?: "default" | "ghost" | "outline";
	showText?: boolean;
	copiedText?: string;
	copyText?: string;
	ariaLabel?: string;
}

const iconSizeClasses = {
	sm: "w-3 h-3",
	md: "w-4 h-4",
	lg: "w-5 h-5",
};

const variantClasses = {
	default: "bg-blue-600 text-white hover:bg-blue-700 border-blue-600",
	ghost: "bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900",
	outline: "bg-transparent text-gray-600 border border-gray-300 hover:bg-gray-50 hover:text-gray-900",
};

export function CopyButton({
	text,
	className,
	iconSize = "md",
	variant = "ghost",
	showText = false,
	copiedText = "Copied!",
	copyText = "Copy",
	ariaLabel,
}: CopyButtonProps) {
	const [copied, setCopied] = useState(false);
	const [tooltipOpen, setTooltipOpen] = useState(false);

	const copyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTooltipOpen(true);
			setTimeout(() => {
				setCopied(false);
				setTooltipOpen(false);
			}, 2000);
		} catch (error) {
			toast.error("Failed to copy text: " + error);
		}
	};

	// Determine the effective aria-label
	const getAriaLabel = () => {
		if (copied) return copiedText;
		if (ariaLabel) return ariaLabel;

		// If text is long, default to a generic message to avoid screen reader spam
		if (text.length > 30) return "Copy to clipboard";

		return `Copy ${text}`;
	};

	const buttonContent = (
		<button
			onClick={copyToClipboard}
			className={cn(
				"cursor-pointer inline-flex items-center gap-2 px-2 py-1 rounded-md transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
				variantClasses[variant],
				className,
			)}
			aria-label={getAriaLabel()}
		>
			{copied ? (
				<Check className={cn(iconSizeClasses[iconSize], "text-green-600")} aria-hidden="true" />
			) : (
				<Copy className={iconSizeClasses[iconSize]} aria-hidden="true" />
			)}
			{showText && <span>{copied ? copiedText : copyText}</span>}
		</button>
	);

	if (showText) {
		return buttonContent;
	}

	return (
		<Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
			<TooltipTrigger
				asChild
				onMouseEnter={() => setTooltipOpen(true)}
				onMouseLeave={() => !copied && setTooltipOpen(false)}
				onFocus={() => setTooltipOpen(true)}
				onBlur={() => setTooltipOpen(false)}
			>
				{buttonContent}
			</TooltipTrigger>
			<TooltipContent>
				<p>{copied ? copiedText : copyText}</p>
			</TooltipContent>
		</Tooltip>
	);
}
